// AuthorizationDrawerContext — mounts one `AuthorizationDrawer` in the
// authenticated shell (sibling of `SourceWizardProvider`) and exposes an
// imperative opener that pre-loads it for a single authorization and reports
// whether the user completed a reconnect. Lets the assistant reconnect card
// (a plain module executor) open the existing reconnect flow — OAuth re-consent
// or a new API key — without duplicating any of that UI (plan 2026-07-29
// assistant-reconnect-flow, steps 2 + 5).
//
// Contract of `openAuthorizationDrawer(authorizationPublicId)` (mirrors
// `SourceWizardContext`):
//   - loads the authorization by its PUBLIC id (`auth_...`), then opens the
//     drawer pre-loaded for it,
//   - resolves `{ reconnected: true }` when the drawer signalled a successful
//     change (it calls `onChanged` after a reconnect / key rotation) during the
//     open,
//   - resolves `null` when the drawer was closed without a change, or when the
//     authorization could not be loaded (a toast explains why).
//
// Only one drawer runs at a time. Calling `openAuthorizationDrawer` while one is
// already open settles the earlier promise with `null` (a cancel of the earlier
// request) and replaces it — the same single-instance policy as the wizard.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { notifications } from "@mantine/notifications";

import { AuthorizationDrawer } from "@/components/authorizations/AuthorizationDrawer";
import { listAuthorizations } from "@/api/authorizations";
import { normalizeAuthorization } from "@/components/authorizations/authorizationMeta";
import { useAuth } from "@/context/AuthContext";
import type { AuthorizationDTO } from "@/types/authorizations";
import {
  setAuthorizationDrawerOpener,
  type AuthorizationDrawerResult,
  type OpenAuthorizationDrawer,
} from "@/context/authorizationDrawerBridge";

interface AuthorizationDrawerContextValue {
  /** Open the shell-mounted drawer pre-loaded for an authorization public id;
   *  resolves `{ reconnected: true }` on a successful reconnect or `null`
   *  otherwise. Shares its type with the module-level bridge so the hook and the
   *  imperative entry point can never drift. */
  openAuthorizationDrawer: OpenAuthorizationDrawer;
}

const AuthorizationDrawerContext =
  createContext<AuthorizationDrawerContextValue>({
    openAuthorizationDrawer: () =>
      Promise.reject(
        new Error("AuthorizationDrawerProvider is not mounted."),
      ),
  });

type Resolver = (result: AuthorizationDrawerResult) => void;

export function AuthorizationDrawerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useAuth();
  const backendUrl = import.meta.env.VITE_API_URL;

  // Same derivation the Authorizations page uses — the drawer's controls stay
  // disabled for non-admins regardless, and the reconnect routes are admin-gated
  // server-side (the card is advisory).
  const isAccountAdmin =
    user?.activeAccount?.role === "OWNER" ||
    user?.activeAccount?.role === "ADMIN";
  const isSuperadmin = !!user?.isSuperadmin;

  const [opened, setOpened] = useState(false);
  const [authorization, setAuthorization] = useState<AuthorizationDTO | null>(
    null,
  );

  // The pending promise's resolver. Held in a ref (not state) so the drawer's
  // stable callbacks can settle it without re-subscribing. Nulled the instant it
  // is consumed — that null check is the double-resolve guard (onChanged may set
  // the changed flag, then onClose fires; only one settle wins).
  const resolverRef = useRef<Resolver | null>(null);
  // Whether the drawer reported a successful change (reconnect / rotate) during
  // THIS open. Read on close to decide `{ reconnected: true }` vs `null`.
  const changedRef = useRef(false);
  // Monotonic id guarding the async load against a superseding open (one-at-a-
  // time policy): a stale load whose id no longer matches must not open the
  // drawer for the wrong authorization.
  const requestIdRef = useRef(0);

  const settle = useCallback((result: AuthorizationDrawerResult) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  // Load the authorization by public id, then open — the drawer never flashes an
  // empty "No authorization selected" state, and the calling card gets a clean
  // "loading" window (it awaits this promise before the drawer appears).
  const loadAndOpen = useCallback(
    async (authorizationPublicId: string, reqId: number) => {
      try {
        const list = await listAuthorizations();
        if (reqId !== requestIdRef.current) return; // superseded by a newer open
        const found =
          list
            .map(normalizeAuthorization)
            .find((a) => a.id === authorizationPublicId) ?? null;
        if (!found) {
          notifications.show({
            color: "yellow",
            message:
              "That connection couldn't be found. It may have been removed.",
          });
          settle(null);
          return;
        }
        setAuthorization(found);
        setOpened(true);
      } catch {
        if (reqId !== requestIdRef.current) return;
        notifications.show({
          color: "red",
          message: "Could not open the connection. Please try again.",
        });
        settle(null);
      }
    },
    [settle],
  );

  const openAuthorizationDrawer = useCallback<OpenAuthorizationDrawer>(
    (authorizationPublicId) =>
      new Promise<AuthorizationDrawerResult>((resolve) => {
        // A drawer is already open (or loading): cancel the earlier request,
        // then replace it.
        settle(null);
        resolverRef.current = resolve;
        changedRef.current = false;
        const reqId = ++requestIdRef.current;
        void loadAndOpen(authorizationPublicId, reqId);
      }),
    [settle, loadAndOpen],
  );

  // The drawer calls `onChanged` after a successful reconnect / key rotation
  // (also on rename/delete). We record it and resolve on close — a reconnect
  // leaves the drawer open (the popup success toast shows first), so close is the
  // natural settle point.
  const handleChanged = useCallback(() => {
    changedRef.current = true;
  }, []);

  const handleClose = useCallback(() => {
    setOpened(false);
    // Unmount the drawer (see the render gate below) — this also tears down its
    // Shopify/Facebook OAuth popup listeners so an idle shell drawer can never
    // intercept an OAuth callback meant for another flow (e.g. the wizard's own
    // Shopify connect).
    setAuthorization(null);
    settle(changedRef.current ? { reconnected: true } : null);
    changedRef.current = false;
  }, [settle]);

  // Publish the imperative opener to the module-level bridge so non-React callers
  // (builtinActions.ts) can reach it. Cleared on unmount; any pending promise is
  // settled as a cancel so a caller never hangs.
  useEffect(() => {
    setAuthorizationDrawerOpener(openAuthorizationDrawer);
    return () => {
      setAuthorizationDrawerOpener(null);
      settle(null);
    };
  }, [openAuthorizationDrawer, settle]);

  const value = useMemo<AuthorizationDrawerContextValue>(
    () => ({ openAuthorizationDrawer }),
    [openAuthorizationDrawer],
  );

  return (
    <AuthorizationDrawerContext.Provider value={value}>
      {children}
      {/* Mounted ONLY while a reconnect is in flight (an authorization is
          loaded). AuthorizationDrawer wires its Shopify/Facebook OAuth popup
          listeners at its component root, so an always-mounted idle drawer would
          intercept OAuth callbacks meant for other flows (the wizard sidesteps
          this by keeping those hooks in conditionally-mounted step components).
          Gating the mount keeps the idle shell free of those listeners. */}
      {authorization && (
        <AuthorizationDrawer
          opened={opened}
          onClose={handleClose}
          backendUrl={backendUrl}
          isAccountAdmin={isAccountAdmin}
          isSuperadmin={isSuperadmin}
          authorization={authorization}
          onChanged={handleChanged}
          // Above the assistant drawer (both default to Mantine's modal z-index
          // 200); 300 stays below notifications (400) so toasts still surface.
          zIndex={300}
        />
      )}
    </AuthorizationDrawerContext.Provider>
  );
}

export function useAuthorizationDrawer() {
  return useContext(AuthorizationDrawerContext);
}
